export const BILLING_ENTRY_EXPORT_COLUMNS = Object.freeze([
    { key: 'date', label: 'Entry date' },
    { key: 'created_date', label: 'Created date' },
    { key: 'description', label: 'Description' },
    { key: 'raw_quantity', label: 'Time / quantity' },
    { key: 'rate', label: 'Rate' },
    { key: 'time_invoice_id', label: 'Time invoice ID' },
    { key: 'id', label: 'Entry ID' },
    { key: 'uuid', label: 'UUID' },
    { key: 'case_id', label: 'Case ID' },
    { key: 'billing_rate_id', label: 'Billing rate ID' },
    { key: 'user_id', label: 'Team member ID' },
    { key: 'user_name', label: 'Team member' },
    { key: 'created_by', label: 'Created by ID' },
    { key: 'created_by_name', label: 'Created by' },
    { key: 'amount', label: 'Amount' },
    { key: 'final_quantity', label: 'Final quantity' },
    { key: 'final_total', label: 'Final total' },
    { key: 'original_total', label: 'Original total' },
    { key: 'item_type', label: 'Item type' },
    { key: 'entry_kind', label: 'Entry kind' },
    { key: 'status', label: 'Status' },
    { key: 'invoice_id', label: 'Invoice ID' },
    { key: 'draft', label: 'Draft' },
    { key: 'billable', label: 'Billable' },
    { key: 'chargeable', label: 'Chargeable' },
    { key: 'deleted', label: 'Deleted' },
    { key: 'time_code', label: 'Time code' },
    { key: 'time_code_2', label: 'Secondary time code' },
    { key: 'expense_code', label: 'Expense code' },
    { key: 'expense_code_2', label: 'Secondary expense code' },
    { key: 'flat_fee_code', label: 'Flat-fee code' },
    { key: 'flat_fee_code_2', label: 'Secondary flat-fee code' },
    { key: 'unit', label: 'Unit' },
]);

export const DEFAULT_BILLING_ENTRY_EXPORT_COLUMNS = Object.freeze([
    'date',
    'created_date',
    'description',
    'raw_quantity',
    'rate',
    'time_invoice_id',
]);

export const createBillingEntryExportFilters = () => ({
    entryDateFrom: '',
    entryDateTo: '',
    createdDateFrom: '',
    createdDateTo: '',
    userId: '',
    createdById: '',
    entryKind: '',
    invoiceState: '',
    status: '',
    minQuantity: '',
    maxQuantity: '',
    description: '',
});

const positiveId = (value) => {
    const numeric = Number(value);
    return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
};

const localCalendarDate = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const year = String(value.getFullYear()).padStart(4, '0');
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    const raw = String(value ?? '').trim();
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
};

export const getBillingEntryCreatedDate = (entry = {}) => (
    entry.created_date
    ?? entry.created_at
    ?? entry.creation_date
    ?? ''
);

const dateIsInRange = (value, from, to) => {
    if (!from && !to) return true;
    const date = localCalendarDate(value);
    if (!date) return false;
    return (!from || date >= from) && (!to || date <= to);
};

const numberIsInRange = (value, minimum, maximum) => {
    if (minimum === '' && maximum === '') return true;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return false;
    if (minimum !== '' && numeric < Number(minimum)) return false;
    if (maximum !== '' && numeric > Number(maximum)) return false;
    return true;
};

export const filterBillingEntriesForExport = (entries = [], filters = {}) => {
    const next = { ...createBillingEntryExportFilters(), ...(filters || {}) };
    const description = String(next.description || '').trim().toLowerCase();

    return (Array.isArray(entries) ? entries : []).filter((entry) => {
        if (!dateIsInRange(entry?.date, next.entryDateFrom, next.entryDateTo)) return false;
        if (!dateIsInRange(getBillingEntryCreatedDate(entry), next.createdDateFrom, next.createdDateTo)) return false;
        if (next.userId !== '' && String(entry?.effective_user_id ?? entry?.user_id ?? '') !== String(next.userId)) return false;
        if (next.createdById !== '' && String(entry?.created_by ?? '') !== String(next.createdById)) return false;
        if (next.entryKind !== '' && String(entry?.item_type || entry?.entry_kind || '') !== next.entryKind) return false;

        const invoiced = positiveId(entry?.time_invoice_id) !== null || positiveId(entry?.invoice_id) !== null;
        if (next.invoiceState === 'invoiced' && !invoiced) return false;
        if (next.invoiceState === 'unbilled' && invoiced) return false;
        if (next.status !== '' && String(entry?.status ?? '') !== String(next.status)) return false;
        if (!numberIsInRange(entry?.raw_quantity, next.minQuantity, next.maxQuantity)) return false;
        if (description && !String(entry?.description || '').toLowerCase().includes(description)) return false;
        return true;
    });
};

export const normalizeBillingEntryExportColumns = (columnKeys = []) => {
    const requested = new Set((Array.isArray(columnKeys) ? columnKeys : []).map(String));
    return BILLING_ENTRY_EXPORT_COLUMNS
        .filter((column) => requested.has(column.key))
        .map((column) => column.key);
};

export const validateBillingEntryExportFilters = (filters = {}) => {
    const next = { ...createBillingEntryExportFilters(), ...(filters || {}) };
    if (next.entryDateFrom && next.entryDateTo && next.entryDateFrom > next.entryDateTo) {
        return 'The entry date start must be on or before the end date.';
    }
    if (next.createdDateFrom && next.createdDateTo && next.createdDateFrom > next.createdDateTo) {
        return 'The created date start must be on or before the end date.';
    }
    if (next.minQuantity !== '' && !Number.isFinite(Number(next.minQuantity))) {
        return 'The minimum time / quantity must be a number.';
    }
    if (next.maxQuantity !== '' && !Number.isFinite(Number(next.maxQuantity))) {
        return 'The maximum time / quantity must be a number.';
    }
    if (next.minQuantity !== '' && next.maxQuantity !== '' && Number(next.minQuantity) > Number(next.maxQuantity)) {
        return 'The minimum time / quantity must not exceed the maximum.';
    }
    return '';
};

export const buildBillingEntriesExportRequest = ({ caseId, columnKeys, filters } = {}) => {
    const normalizedCaseId = positiveId(caseId);
    if (!normalizedCaseId) throw new Error('A valid case is required.');
    const columns = normalizeBillingEntryExportColumns(columnKeys);
    if (columns.length === 0) throw new Error('Select at least one column to export.');

    const next = { ...createBillingEntryExportFilters(), ...(filters || {}) };
    const filterError = validateBillingEntryExportFilters(next);
    if (filterError) throw new Error(filterError);

    return {
        case_id: normalizedCaseId,
        columns,
        filters: {
            entry_date_from: next.entryDateFrom,
            entry_date_to: next.entryDateTo,
            created_date_from: next.createdDateFrom,
            created_date_to: next.createdDateTo,
            user_id: next.userId,
            created_by_id: next.createdById,
            entry_kind: next.entryKind,
            invoice_state: next.invoiceState,
            status: next.status,
            min_quantity: next.minQuantity,
            max_quantity: next.maxQuantity,
            description: next.description,
        },
    };
};

export const buildBillingEntriesExportFilename = (caseId, now = new Date()) => {
    const caseLabel = positiveId(caseId) || 'unknown-case';
    return `Case ${caseLabel} Billing Entries ${localCalendarDate(now) || 'export'}.csv`;
};

export const billingEntriesExportFilenameFromResponse = (response, fallback) => {
    const disposition = response?.headers?.get?.('Content-Disposition') || '';
    const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1].trim());
        } catch (error) {
            // Fall through to the ASCII filename or the local fallback.
        }
    }
    const ascii = disposition.match(/filename="([^"]+)"/i) || disposition.match(/filename=([^;]+)/i);
    return String(ascii?.[1] || fallback || 'billing-entries.csv').trim();
};

export const downloadBillingEntriesExport = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
